import { useEffect, useRef, useState } from 'react';
import { toCanvas } from 'qrcode';

export interface QrCodeProps {
  /**
   * Payload to encode. Per spec §5 Page 2 this is the generated managed or
   * direct download URL — NEVER a secret API token.
   */
  value: string;
  /** Canvas edge length in CSS pixels. Defaults to 192. */
  size?: number;
  /** Accessible description of the encoded content. */
  label?: string;
}

/**
 * Client-only QR renderer (spec §5 Page 2, §8 src/components/ QR renderer).
 * Encodes entirely in the browser via the `qrcode` package — no payload ever
 * leaves the page for a third-party QR service. Error-correction level M
 * keeps managed-link URLs crisp; very long direct GCS V4 URLs that exceed QR
 * byte capacity surface a visible fallback instead of a broken image.
 */
export default function QrCode({ value, size = 192, label = 'Download URL QR code' }: QrCodeProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    if (value === '') {
      setError('Nothing to encode');
      return;
    }
    let cancelled = false;
    toCanvas(canvas, value, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: size,
    }).then(
      () => {
        if (!cancelled) setError(null);
      },
      (err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'QR encoding failed');
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [value, size]);

  return (
    <figure className="qr-code" aria-label={label}>
      <canvas
        ref={canvasRef}
        className="qr-code-canvas"
        role="img"
        aria-label={label}
        width={size}
        height={size}
        hidden={error !== null}
      />
      {error !== null && (
        <figcaption className="qr-code-error" role="alert">
          QR unavailable: {error}
        </figcaption>
      )}
    </figure>
  );
}
